import { useMemo, useState } from 'react';
import { IconFileText, IconHeadphones, IconMaximize, IconPhotoOff, IconVideoOff } from '@tabler/icons-react';
import { PROCEDURES_MEDIA_TYPE, type ProcedureMediaView } from '@actschurch/ccms-sdk';
import { HlsVideo } from './HlsVideo';
import { ImageLightbox, type LightboxImage } from './ImageLightbox';

export interface StepMediaProps {
  media: ProcedureMediaView[];
  /** Fallback alt text ("Step 3: Switch on the main breaker"). */
  label: string;
  /** Called when a signed link has expired (image fails while online): the page refetches to renew. */
  onExpired?: () => void;
  /** Smaller first image (overview cover). */
  compact?: boolean;
}

function sorted(media: ProcedureMediaView[]): ProcedureMediaView[] {
  return [...media].sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Everything attached to a step: photos full width (tap to zoom), HLS video (falls back to the
 * original upload while it is still processing), audio clips and documents.
 */
export function StepMedia({ media, label, onExpired, compact }: StepMediaProps) {
  const list = useMemo(() => sorted(media), [media]);
  const images = list.filter((m) => m.mediaTypeID === PROCEDURES_MEDIA_TYPE.Image && (m.url || m.thumbUrl));
  const videos = list.filter((m) => m.mediaTypeID === PROCEDURES_MEDIA_TYPE.Video);
  const audios = list.filter((m) => m.mediaTypeID === PROCEDURES_MEDIA_TYPE.Audio && m.url);
  const docs = list.filter((m) => m.mediaTypeID === PROCEDURES_MEDIA_TYPE.Document && m.url);
  const [open, setOpen] = useState<number | null>(null);
  // Keyed by URL: a refetched (renewed) link gets a fresh try.
  const [broken, setBroken] = useState<Record<string, boolean>>({});

  const lightbox: LightboxImage[] = images.map((m, i) => ({
    src: (m.url || m.thumbUrl) as string,
    alt: m.altText || m.caption || (images.length > 1 ? `${label}, photo ${i + 1}` : label),
    caption: m.caption,
  }));

  if (list.length === 0) return null;

  return (
    <div className={`proc-media${compact ? ' compact' : ''}`}>
      {images.map((m, i) => (
        <figure key={m.mediaID} className="proc-photo">
          {broken[(m.url || m.thumbUrl) as string] ? (
            <div className="proc-photo-missing">
              <IconPhotoOff size={28} aria-hidden="true" />
              <span>This photo can't load right now. It will come back when you have signal.</span>
            </div>
          ) : (
            <button
              type="button"
              className="proc-photo-btn"
              onClick={() => setOpen(i)}
              aria-label={`Open photo${images.length > 1 ? ` ${i + 1} of ${images.length}` : ''} full screen: ${lightbox[i].alt}`}
            >
              <img
                src={(m.url || m.thumbUrl) as string}
                alt={lightbox[i].alt}
                width={m.width ?? undefined}
                height={m.height ?? undefined}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                onError={() => {
                  const src = (m.url || m.thumbUrl) as string;
                  setBroken((b) => ({ ...b, [src]: true }));
                  if (typeof navigator === 'undefined' || navigator.onLine !== false) {
                    forgetCachedPhoto(src);
                    onExpired?.();
                  }
                }}
              />
              <span className="zoom" aria-hidden="true"><IconMaximize size={18} /> Tap to zoom</span>
            </button>
          )}
          {m.caption && <figcaption>{m.caption}</figcaption>}
        </figure>
      ))}

      {videos.map((m) => {
        const v = m.video;
        const hls = v?.status === 'ready' ? v.hlsUrl : null;
        const fallback = v?.sourceUrl || undefined;
        if (!hls && !fallback) {
          return (
            <div key={m.mediaID} className="proc-photo-missing">
              <IconVideoOff size={28} aria-hidden="true" />
              <span>{v?.status === 'failed' ? "This video isn't available." : 'This video is still being prepared. Please check again in a few minutes.'}</span>
            </div>
          );
        }
        return (
          <figure key={m.mediaID} className="proc-video">
            <HlsVideo
              hlsUrl={hls}
              fallbackSrc={fallback}
              poster={v?.posterUrl || undefined}
              controls
              playsInline
              preload="metadata"
              aria-label={m.altText || m.caption || `${label}, video`}
            >
              {v?.captionsVttUrl && <track kind="captions" src={v.captionsVttUrl} srcLang="en" label="English" default />}
            </HlsVideo>
            {m.caption && <figcaption>{m.caption}</figcaption>}
          </figure>
        );
      })}

      {audios.map((m) => (
        <figure key={m.mediaID} className="proc-audio">
          <span className="head"><IconHeadphones size={20} aria-hidden="true" /> {m.caption || m.fileName || 'Listen'}</span>
          <audio controls preload="none" src={m.url as string} aria-label={m.altText || m.caption || `${label}, audio`} />
        </figure>
      ))}

      {docs.map((m) => (
        <a key={m.mediaID} className="proc-docrow" href={m.url as string} target="_blank" rel="noopener noreferrer">
          <span className="ic" aria-hidden="true"><IconFileText size={22} /></span>
          <span className="meta">
            <b>{m.caption || m.fileName}</b>
            <span>Opens in a new tab</span>
          </span>
        </a>
      ))}

      <ImageLightbox images={lightbox} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />
    </div>
  );
}

/** Drop a bad copy of a photo from the service worker cache (stored without the signed query string). */
export function forgetCachedPhoto(src: string): void {
  try {
    if (typeof caches === 'undefined') return;
    const u = new URL(src, window.location.href);
    void caches.open('procedures-images').then((c) => c.delete(`${u.origin}${u.pathname}`)).catch(() => undefined);
  } catch {
    /* ignore */
  }
}

/** Warm the photo cache (service worker, query string stripped) so later steps open without signal. */
export function prefetchImages(urls: (string | null | undefined)[]): void {
  if (typeof window === 'undefined' || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
  const unique = Array.from(new Set(urls.filter((u): u is string => !!u)));
  const run = () => {
    unique.forEach((u) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = u;
    });
  };
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
  if (idle) idle(run);
  else window.setTimeout(run, 400);
}
